import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import { ModuleType } from "entities/modules/types";
import { usePageModules } from "modules/brands/pages/ModulesContainer/components/PageModules/context";
import React from "react";
import { Draggable } from "react-beautiful-dnd";
import { ModuleComponentMap, ModuleHeightMap } from "../../constants";
import { StyledTileRow } from "./styled";

const getItemStyle = (
  isDragging: boolean,
  draggableStyle: React.CSSProperties,
  opacity?: number,
) => {
  const padding = 36;
  return {
    ...draggableStyle,
    top: draggableStyle.top ? Number(draggableStyle.top) - padding : undefined,
    ...(isDragging && {
      background: "transparent",
    }),
    ...(opacity !== undefined && { opacity }),
  };
};

type DraggableRowProps<T> = {
  item: T & { id: string; type?: ModuleType; opacity?: number };
  index: number;
  disabled: boolean;
  isLast?: boolean;
  draggableHidden?: boolean;
  onRowClick?: (item: T) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  handleDeleteModuleClick?: (item: T) => void;
  isLoading: boolean;
  newlyGeneratedModuleId?: string;
  isEntering?: boolean;
};

export default function DraggableRow<T extends { id: string }>(
  props: DraggableRowProps<T>,
) {
  const { onUpdateStatus } = usePageModules();
  const Component = ModuleComponentMap[props.item.type as ModuleType];
  const moduleHeight = ModuleHeightMap[props.item.type as ModuleType];
  const isNewItem = props.item.id === props.newlyGeneratedModuleId;

  if (!Component) {
    console.warn(`No component found for module type: ${props.item.type}`);
    return null;
  }

  return (
    <Draggable draggableId={props.item.id} index={props.index}>
      {(provided, snapshot) => (
        <StyledTileRow
          ref={provided.innerRef}
          index={props.index}
          {...provided.draggableProps}
          isDragging={snapshot.isDragging}
          className={`
            ${isNewItem && props.isEntering ? "entering" : ""}
            ${!isNewItem && props.newlyGeneratedModuleId && props.isEntering ? "shifting" : ""}
          `.trim()}
          style={getItemStyle(
            snapshot.isDragging,
            provided.draggableProps.style || {},
            props.item.opacity,
          )}
          shiftHeight={moduleHeight}
          onClick={() => props.onRowClick && props.onRowClick(props.item)}
          onMouseEnter={props.onMouseEnter}
          onMouseLeave={props.onMouseLeave}
        >
          <TableCell
            align="left"
            style={{
              width: 24,
              display: props.draggableHidden ? "none" : "table-cell",
            }}
          >
            <IconButton {...provided.dragHandleProps} disabled={props.disabled}>
              <DragIndicatorIcon />
            </IconButton>
          </TableCell>
          <TableCell key={props.item.id}>
            <Component
              isLoading={props.isLoading}
              data={props.item}
              onEditComponent={() =>
                props.onRowClick && props.onRowClick(props.item)
              }
              onDeleteComponent={() =>
                props.handleDeleteModuleClick &&
                props.handleDeleteModuleClick(props.item)
              }
              onUpdateStatus={onUpdateStatus}
              isNewlyGenerated={isNewItem}
            />
          </TableCell>
        </StyledTileRow>
      )}
    </Draggable>
  );
}
